import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { ColorPalette } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import type { MonthlyLopSummary } from '../types/leave';
import { formatLeaveDays } from '../utils/lateArrivalRules';
import { formatPayrollMonth, lopBreakdownRows } from '../utils/lopSummary';

type Props = {
  summary: MonthlyLopSummary;
  palette: ColorPalette;
};

/** Server-calculated LOP for one payroll month, split by source. */
export function LopMonthSummary({ summary, palette }: Props) {
  const month = formatPayrollMonth(summary.payrollMonth);
  const rows = lopBreakdownRows(summary);
  const title = summary.isCurrentMonth || !month ? 'LOP This Month' : `LOP · ${month}`;
  return (
    <View
      accessibilityLabel={`${title}: ${formatLeaveDays(summary.totalLopDays)}`}
      style={styles.block}
    >
      <View style={styles.header}>
        <Text style={[styles.title, { color: palette.textPrimary }]}>{title}</Text>
        <Text
          style={[
            styles.total,
            { color: summary.totalLopDays > 0 ? palette.error : palette.textPrimary },
          ]}
        >
          {formatLeaveDays(summary.totalLopDays)}
        </Text>
      </View>
      {rows.map(row => (
        <View key={row.key} style={styles.row}>
          <Text style={[styles.caption, { color: palette.textSecondary }]}>{row.label}</Text>
          <Text style={[styles.caption, { color: palette.textPrimary }]}>
            {formatLeaveDays(row.days)}
          </Text>
        </View>
      ))}
      {summary.pendingAppliedLopDays > 0 ? (
        <Text style={[styles.caption, { color: palette.textMuted }]}>
          {formatLeaveDays(summary.pendingAppliedLopDays)} of LOP awaiting approval (not counted yet)
        </Text>
      ) : null}
      {summary.isProvisional ? (
        <Text style={[styles.caption, { color: palette.textMuted }]}>
          A negative leave balance is not carried forward. It becomes final LOP for this month at
          month end.
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  block: { paddingTop: spacing.md, gap: spacing.xs },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' },
  row: { flexDirection: 'row', justifyContent: 'space-between', gap: spacing.md },
  title: { ...typography.body, fontWeight: '700' },
  total: { ...typography.body, fontWeight: '700' },
  caption: { ...typography.caption, flexShrink: 1 },
});
